"use client";

import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";

/**
 * Floating helper with a fixed set of questions — deliberately not an AI.
 * Every answer comes from copy the team controls, and anything it can't
 * answer routes to the booking page.
 */
const QUESTION_KEYS = ["services", "products", "timeline", "pricing", "support", "contact"] as const;

type Entry = { id: number; from: "bot" | "user"; text: string };

export default function SiteAssistant() {
  const t = useTranslations("Assistant");
  const locale = useLocale();
  const isArabic = locale === "ar";

  const [isOpen, setIsOpen] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const [asked, setAsked] = useState<string[]>([]);
  const [log, setLog] = useState<Entry[]>([]);
  const nextId = useRef(0);
  const logRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Greet once the visitor has settled, then withdraw so the bubble does not
  // sit over page content — it covered the stats on mobile (team notes, 15/10).
  useEffect(() => {
    const show = window.setTimeout(() => setShowNudge(true), 6000);
    const hide = window.setTimeout(() => setShowNudge(false), 16000);
    return () => {
      window.clearTimeout(show);
      window.clearTimeout(hide);
    };
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
    setShowNudge(false);
    setLog((entries) =>
      entries.length
        ? entries
        : [{ id: nextId.current++, from: "bot", text: t("greeting") }],
    );
  }, [t]);

  // Escape closes; clicking outside closes.
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!panelRef.current?.contains(event.target as Node)) setIsOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [isOpen]);

  // Keep the newest message in view.
  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
  }, [log]);

  const ask = (key: string) => {
    setAsked((previous) => [...previous, key]);
    setLog((entries) => [
      ...entries,
      { id: nextId.current++, from: "user", text: t(`questions.${key}.q`) },
      { id: nextId.current++, from: "bot", text: t(`questions.${key}.a`) },
    ]);
  };

  const remaining = QUESTION_KEYS.filter((key) => !asked.includes(key));

  return (
    <div dir={isArabic ? "rtl" : "ltr"} className="assistant">
      {isOpen ? (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-label={t("title")}
          className="assistant__panel"
        >
          <div className="assistant__head">
            <span className="assistant__avatar">
              <Image src="/assets/images/sabah/robot-cta.png" alt="" width={36} height={36} className="size-9 object-contain" />
            </span>
            <span className="assistant__title">{t("title")}</span>
            <button type="button" onClick={() => setIsOpen(false)} aria-label={t("close")} className="assistant__close">
              ✕
            </button>
          </div>

          <div ref={logRef} className="assistant__log" aria-live="polite">
            {log.map((entry) => (
              <p key={entry.id} className={`assistant__msg assistant__msg--${entry.from}`}>
                {entry.text}
              </p>
            ))}
          </div>

          <div className="assistant__chips">
            {remaining.map((key) => (
              <button key={key} type="button" onClick={() => ask(key)} className="assistant__chip">
                {t(`questions.${key}.q`)}
              </button>
            ))}
            <Link href={`/${locale}/booking`} className="assistant__chip assistant__chip--cta">
              {t("bookCta")}
            </Link>
          </div>

          <p className="assistant__foot">{t("disclaimer")}</p>
        </div>
      ) : null}

      {showNudge && !isOpen ? (
        <button type="button" onClick={open} className="assistant__nudge">
          {t("nudge")}
        </button>
      ) : null}

      <button
        type="button"
        onClick={() => (isOpen ? setIsOpen(false) : open())}
        aria-expanded={isOpen}
        aria-label={t("open")}
        className="assistant__btn"
      >
        <Image src="/assets/images/sabah/robot-cta.png" alt="" width={64} height={64} className="size-12 object-contain" />
      </button>
    </div>
  );
}
